import { useAppConstantsContext } from '@/context/AppConstants';
import { DataCard, openReportInNewTab } from '@/reports/index';
import {
  build as buildDataCardBadge,
  DataCardBadgeTruthinessMapping,
} from '@/reports/shared/DataCard/DataCardBadge/DataCardBadge';
import {
  build as buildTag,
  DataCardTagTruthinessMapping,
} from '@/reports/shared/DataCard/DataCardTag/DataCardTag';
import { commonTypes } from '@/reports/types';
import { DataList } from '@/theme/index';
import { nameize } from '@/utils/index';
import { isNewInfoItem } from '@/utils/isNewInfoItem/isNewInfoItem';

type EmailWithNewInfo = commonTypes.Email & { new_info: boolean };

interface EmailCardProps {
  email: EmailWithNewInfo;
  thumbRating: () => JSX.Element;
  ownerName: string;
}

export function EmailCard({ email, thumbRating, ownerName }: EmailCardProps) {
  const {
    links: { baseUrl },
    config: { higherConfidenceThreshold, enabledReportTypes = [] },
  } = useAppConstantsContext();

  const badge = () => {
    const dataConfidence = email?.meta?.confidence || 0;
    const owner = nameize(ownerName ?? '');

    const badgeMapping: DataCardBadgeTruthinessMapping[] = [
      {
        name: 'bestMatch',
        truthiness: email?.meta?.is_best || false,
        insert: 'Email',
      },
      {
        name: 'higherConfidence',
        truthiness: dataConfidence >= higherConfidenceThreshold,
        insert: owner,
      },
    ];

    return buildDataCardBadge(badgeMapping);
  };

  const tag = () => {
    const tagMapping: DataCardTagTruthinessMapping[] = [
      {
        name: 'newInfo',
        truthiness: isNewInfoItem({
          newInfo: email?.new_info,
          monitoredChangeType: email?.meta?.monitored_change_type,
          monitoredOn: email?.meta?.monitored_on,
        }),
      },
    ];

    return buildTag(tagMapping);
  };

  const clickableStyles = {
    marginBottom: 0,
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    textDecoration: 'underline',
    '&:hover': {
      cursor: 'pointer',
    },
  };

  let isEmailReportEnabled = true;
  if (enabledReportTypes.length > 0) {
    isEmailReportEnabled = enabledReportTypes.includes('email');
  }

  return (
    <DataCard
      tag={tag()}
      badge={badge()}
      thumbRating={thumbRating}
      titleProps={{
        title: email.address?.toLowerCase() || '',
        onClick: () =>
          isEmailReportEnabled &&
          openReportInNewTab(
            {
              email: email.address || '',
              searchType: 'email',
            },
            baseUrl
          ),
        sx: isEmailReportEnabled ? clickableStyles : {},
      }}
      buttonProps={
        isEmailReportEnabled
          ? {
              label: 'View Email Report',
              onClick: () =>
                openReportInNewTab(
                  {
                    email: email.address || '',
                    searchType: 'email',
                  },
                  baseUrl
                ),
            }
          : {}
      }
      cardStyles={{ mb: 0 }}
    >
      <DataList items={[['Email Type', email.type]]} />
    </DataCard>
  );
}
